import { useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import cx from 'classnames'
import { isBefore, startOfDay } from 'date-fns'

import Button from 'common/components/button'
import { options } from 'common/components/dateRange/defaultOptions'
import type {
  CustomProps,
  FooterProps,
  SelectorDate,
} from 'common/components/dateRange/interfaces'

import Tooltip from '../Tooltip'

import 'react-datepicker/dist/react-datepicker.css'
import '../datePicker/DatePickerOverrides.css'

const doDaysMatch = (date1: SelectorDate, date2: SelectorDate) => {
  if (date1 === null && date2 === null) {
    return true
  }
  if (!date1 || !date2) {
    return false
  }
  return startOfDay(date1).getTime() === startOfDay(date2).getTime()
}

export type DateRangeProps = {
  startDate: SelectorDate
  endDate: SelectorDate
  onDatesChange: (params: {
    startDate: SelectorDate
    endDate: SelectorDate
  }) => void
  onFocusChange?: (focusedInput: 'startDate' | 'endDate' | null) => void
  focusedInput?: 'startDate' | 'endDate' | null
  onOutsideClick?: () => void
  disabled?: boolean
  isOutsideRange?: (date: Date) => boolean
  minDate?: Date
  maxDate?: Date
  showClearDates?: boolean
  firstDayOfWeek?: number
} & CustomProps

const DateRange = ({
  startDate,
  endDate,
  onDatesChange,
  onOutsideClick,
  disabled,
  isOutsideRange,
  minDate,
  maxDate,
  footer,
  hasTooltip = false,
  tooltipText,
  showClearDates,
}: DateRangeProps) => {
  const [open, setOpen] = useState<'start' | 'end' | null>(null)

  const filterDate = isOutsideRange
    ? (d: Date) => !isOutsideRange(d)
    : undefined

  const handleStartChange = (date: Date | null) => {
    onDatesChange({ startDate: date, endDate })
    if (date) {
      setOpen('end')
    }
  }

  const handleEndChange = (date: Date | null) => {
    onDatesChange({ startDate, endDate: date })
    setOpen(null)
  }

  const handleClear = () => {
    onDatesChange({ startDate: null, endDate: null })
    onOutsideClick?.()
  }

  return (
    <Tooltip show={hasTooltip} overlay={tooltipText}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <ReactDatePicker
            selected={startDate}
            onChange={handleStartChange}
            selectsStart
            startDate={startDate ?? undefined}
            endDate={endDate ?? undefined}
            filterDate={filterDate}
            minDate={minDate}
            maxDate={maxDate ?? endDate ?? undefined}
            disabled={disabled}
            dateFormat="dd MMM yyyy"
            placeholderText="Start date"
            aria-label="Start Date"
            className="border border-gold-400 rounded-sm px-3 py-2 text-sm w-full"
            open={open === 'start' || undefined}
            onInputClick={() => setOpen('start')}
            onClickOutside={() => {
              if (open === 'start') {
                setOpen(null)
                onOutsideClick?.()
              }
            }}
            previousMonthButtonLabel="←"
            nextMonthButtonLabel="→"
          />
          <span className="text-gold-700">→</span>
          <ReactDatePicker
            selected={endDate}
            onChange={handleEndChange}
            selectsEnd
            startDate={startDate ?? undefined}
            endDate={endDate ?? undefined}
            filterDate={filterDate}
            minDate={minDate ?? startDate ?? undefined}
            maxDate={maxDate}
            disabled={disabled}
            dateFormat="dd MMM yyyy"
            placeholderText="End date"
            aria-label="End Date"
            className="border border-gold-400 rounded-sm px-3 py-2 text-sm w-full"
            open={open === 'end' || undefined}
            onInputClick={() => setOpen('end')}
            onClickOutside={() => {
              if (open === 'end') {
                setOpen(null)
                onOutsideClick?.()
              }
            }}
            previousMonthButtonLabel="←"
            nextMonthButtonLabel="→"
          />
          {showClearDates && (startDate || endDate) && (
            <button
              type="button"
              className="text-gold-700 hover:text-base-700 text-xs"
              onClick={handleClear}
            >
              ✕
            </button>
          )}
        </div>

        {footer && (
          <div
            className={cx('flex items-center pt-2', {
              'justify-between': footer.applyDefaultOption,
              'justify-end': !footer.applyDefaultOption,
            })}
          >
            {footer.applyDefaultOption && (
              <div>
                {options.map(({ id, title, start, end }) => {
                  const isSelected =
                    doDaysMatch(startDate, start) && doDaysMatch(endDate, end)
                  return (
                    <Button
                      data-testid={`option-${id}`}
                      data-cy={`default-date-option-${id}`}
                      key={title}
                      onClick={() => footer.applyDefaultOption!(start, end)}
                      disabled={footer.areDefaultOptionsDisabled}
                      className={cx(
                        'mr-2',
                        { 'text-gold-600': !isSelected },
                        { 'text-base-700': isSelected }
                      )}
                      variant="link"
                      size="medium"
                    >
                      {title}
                    </Button>
                  )
                })}
              </div>
            )}
            {(footer.hasDateChanged ?? (startDate || endDate)) && (
              <div>
                <Button
                  className="uppercase underline mr-2 text-gold-600"
                  variant="link"
                  data-testid="cancel"
                  data-cy="date-selector-cancel"
                  onClick={() => footer.onCancel()}
                >
                  Cancel
                </Button>
                <Button
                  className="uppercase"
                  variant="primary"
                  data-testid="apply-dates"
                  data-cy="date-selector-apply"
                  disabled={footer.isApplyDisabled}
                  onClick={() => footer.onApply()}
                >
                  Apply
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Tooltip>
  )
}

export default DateRange
